import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase, isSupabaseEnabled } from "../lib/supabaseClient";
import { genericizeCredentialError } from "../lib/authErrors";
import { isEmailNotConfirmedError } from "../lib/otp";
import { resolveClientRole } from "../lib/roleCheck";
import PoweredByFooter from "../components/PoweredByFooter";
import VolunteerBrandMark from "../components/VolunteerBrandMark";
import AuthScreenHeader from "../components/AuthScreenHeader";
import Spinner from "../components/Spinner";

// The parish brand mark, sized for the auth header — passed to
// AuthScreenHeader on every volunteer auth state so the parish logo
// (or Sacristy fallback) is the persistent mark above each title.
const volunteerBrand = <VolunteerBrandMark height={72} />;

export default function VolunteerLogin() {
  const { parish: slug } = useParams();

  if (!isSupabaseEnabled) {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            brand={volunteerBrand}
            title="Sign in"
            subtitle="Volunteer sign-in isn't available yet. Please check back soon."
          />
        </div>
      </main>
    );
  }

  return <VolunteerLoginForm slug={slug ?? ""} />;
}

type LoginStatus = "idle" | "signing-in" | "error";
type ResetStatus = "idle" | "sending" | "sent" | "error";

function VolunteerLoginForm({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Set by SignIn.tsx when admin credentials land here after a
  // wrong-portal redirect (see submit() below for the reverse case).
  const incomingAuthError = (location.state as { authError?: string } | null)?.authError ?? null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<LoginStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  // Set when sign-in failed only because the email was never
  // confirmed — renders a "finish setting up" link to signup.
  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  // "Forgot password?" reveals an inline request form in place of the
  // sign-in form (same low-friction toggle pattern as the admin
  // SignIn's password/magic-link modes) rather than a separate route
  // — one less navigation for something that's already a detour.
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState<ResetStatus>("idle");
  const [resetError, setResetError] = useState<string | null>(null);

  // Already signed in? Don't show the login form — send them to their
  // dashboard. Without this, a signed-in volunteer who taps "Sign in"
  // anywhere (e.g. from the master calendar) just gets the form again:
  // a sign-in loop. "checking" gates the form so it doesn't flash
  // before the redirect.
  const [sessionChecked, setSessionChecked] = useState(false);
  useEffect(() => {
    if (!supabase) {
      setSessionChecked(true);
      return;
    }
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session) navigate(`/${slug}/dashboard`, { replace: true });
      else setSessionChecked(true);
    });
    return () => {
      active = false;
    };
  }, [navigate, slug]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    setStatus("signing-in");
    setError(null);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      // An unconfirmed account (e.g. a scanner ate their original
      // confirmation email) is a fixable state, not a bad password.
      // Send them to signup, which re-sends a fresh 6-digit code for
      // an existing unconfirmed email (see lib/otp.ts semantics).
      if (isEmailNotConfirmedError(signInError.message)) {
        setStatus("error");
        setError("Your account just needs one more step: a confirmation code by email.");
        setNeedsConfirmation(true);
        return;
      }
      setStatus("error");
      setError(genericizeCredentialError(signInError.message));
      return;
    }

    // Symmetric check to SignIn.tsx's — the worker is the actual
    // security boundary either way, this just avoids handing an
    // admin a broken-looking volunteer dashboard (no volunteer_accounts
    // row → the dashboard's own notVolunteerFallback would catch it
    // too, but this is faster and gives clearer next-step copy).
    const role = data.user ? await resolveClientRole(data.user.id) : "none";
    if (role === "admin") {
      await supabase.auth.signOut();
      navigate(`/${slug}/admin/login`, {
        state: {
          authError: "This login is for volunteers — parish staff sign in here.",
        },
      });
      return;
    }
    navigate(`/${slug}/dashboard`);
  }

  async function submitReset(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    setResetStatus("sending");
    setResetError(null);
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/${slug}/reset-password`,
    });
    // Supabase's resetPasswordForEmail doesn't distinguish "no such
    // user" from success (by design — that's the enumeration
    // protection), so a genuine error here is a real failure
    // (network, rate limit), not "email not found". The confirmation
    // copy below stays neutral either way.
    if (resetErr) {
      setResetStatus("error");
      setResetError(resetErr.message);
      return;
    }
    setResetStatus("sent");
  }

  function backToSignIn() {
    setShowForgot(false);
    setResetStatus("idle");
    setResetError(null);
  }

  // Hold the form until we know whether there's already a session (a
  // signed-in visitor is being redirected to their dashboard above).
  if (!sessionChecked) {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader brand={volunteerBrand} title="Sign in" />
          <Spinner label="Loading" />
        </div>
      </main>
    );
  }

  if (showForgot) {
    if (resetStatus === "sent") {
      return (
        <main className="page page--sign-in">
          <div className="sign-in">
            <AuthScreenHeader brand={volunteerBrand} title="Check your email" />
            <div className="sign-in__confirmation">
              <p>
                If an account exists for <strong>{resetEmail}</strong>, we've sent a link to reset
                your password.
              </p>
            </div>
            <div className="sign-in__alt">
              <button type="button" className="sign-in__link" onClick={backToSignIn}>
                Back to sign in
              </button>
            </div>
          </div>
          <PoweredByFooter />
        </main>
      );
    }

    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            brand={volunteerBrand}
            title="Reset your password"
            subtitle="Enter your email and we'll send you a link to reset it."
          />

          <form className="sign-in__form" onSubmit={submitReset}>
            <label className="sign-in__field">
              <span className="sign-in__label">Email</span>
              <input
                type="email"
                className="sign-in__input"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoComplete="email"
                required
                autoFocus
              />
            </label>
            <button type="submit" className="btn" disabled={resetStatus === "sending"}>
              {resetStatus === "sending" ? "Sending…" : "Send reset link"}
            </button>
            {resetStatus === "error" ? <p className="sign-in__error">{resetError}</p> : null}
          </form>

          <div className="sign-in__alt">
            <button type="button" className="sign-in__link" onClick={backToSignIn}>
              Back to sign in
            </button>
          </div>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <AuthScreenHeader
          brand={volunteerBrand}
          title="Sign in"
          subtitle="See your Mass schedule and who you're serving with."
        />
        {incomingAuthError ? <p className="sign-in__error">{incomingAuthError}</p> : null}

        <form className="sign-in__form" onSubmit={submit}>
          <label className="sign-in__field">
            <span className="sign-in__label">Email</span>
            <input
              type="email"
              className="sign-in__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              autoFocus
            />
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Password</span>
            <input
              type="password"
              className="sign-in__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <div className="sign-in__alt">
            <button
              type="button"
              className="sign-in__link"
              onClick={() => {
                setResetEmail(email);
                setShowForgot(true);
              }}
            >
              Forgot password?
            </button>
          </div>
          <button type="submit" className="btn" disabled={status === "signing-in"}>
            {status === "signing-in" ? "Signing in…" : "Sign in"}
          </button>
          {error ? <p className="sign-in__error">{error}</p> : null}
          {needsConfirmation ? (
            <div className="sign-in__alt">
              <Link className="sign-in__link" to={`/${slug}/signup`}>
                Finish setting up your account
              </Link>
            </div>
          ) : null}
        </form>

        <div className="sign-in__alt">
          <span className="sign-in__lede">New here? </span>
          <Link className="sign-in__link" to={`/${slug}/signup`}>
            Create an account
          </Link>
        </div>
      </div>
      <PoweredByFooter />
    </main>
  );
}
